import { useTranslations, cx } from "@jugl-web/utils";
import { Tooltip, Switch } from "@jugl-web/ui-kit";
import { WorkspaceFeature } from "@web-src/modules/workspace/hooks/useWorkspaceFeatureCardData/useWorkspaceFeatureCardData";
import { useCallback, useState } from "react";

export const WorkspaceFeatureCard: React.FC<
  WorkspaceFeature & {
    onReadMoreClick: () => void;
    onOpenAlert: () => void;
    onOpenPaidFeatureDialog: () => void;
  }
> = ({
  title,
  description,
  image,
  isUpcoming,
  isCoreFeature,
  isEnabled,
  isNew,
  isLicensed,
  onToggle,
  onReadMoreClick,
  onOpenAlert,
  onOpenPaidFeatureDialog,
}) => {
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = useState(false);

  const handleSwitchStatus = useCallback(async () => {
    if (isEnabled) {
      onOpenAlert();
      return;
    }
    try {
      setIsLoading(true);
      await onToggle?.();
    } catch {
      // Do nothing
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled, onOpenAlert, onToggle]);

  return (
    <div className="relative m-2 w-[265px] overflow-hidden rounded-xl border-[1px] border-solid border-[#DEDFE0] font-secondary">
      {isUpcoming && (
        <div className="absolute top-3 right-3 h-[26px] rounded-[30px] bg-[#FFE2A8] px-4 drop-shadow-[0_4px_4px_rgba(0,0,0,0.10)]">
          <span className="text-xs font-medium text-[#503600]">
            {t({
              id: "workspace-features-showcase.upcoming",
              defaultMessage: "Upcoming",
            })}
          </span>
        </div>
      )}
      {isNew && (
        <div className="absolute top-3 right-3 h-[26px] rounded-[30px] bg-secondary-200 px-4 drop-shadow-[0_4px_4px_rgba(0,0,0,0.10)]">
          <span className="text-xs font-medium text-secondary-900">
            {t({
              id: "workspace-features-showcase.new",
              defaultMessage: "New",
            })}
          </span>
        </div>
      )}
      <div className="h-[150px] w-full overflow-hidden border-0 border-b border-solid border-[#E4E4E4] bg-[linear-gradient(153deg,_#FFF3FB_9.62%,_#C0DAFD_73.69%,_#81C2FF_80.24%)] pt-4 pl-4">
        <div className="rounded-tl-lg border-0 border-t border-l border-solid border-white pt-2.5 pl-2.5">
          <img src={image} alt={title} />
        </div>
      </div>
      <div className="flex flex-col gap-2.5 p-4 pb-6">
        <div className="flex items-center justify-between">
          <div className="font-medium text-black">{title}</div>
          {!isUpcoming && (
            <Tooltip
              className="!z-[2000] text-center"
              placement="top-start"
              delay="none"
              isDisabled={!isCoreFeature}
              renderTrigger={({ props, ref }) => (
                <div ref={ref} {...props}>
                  <Switch
                    {...props}
                    isChecked={isCoreFeature || isEnabled || false}
                    onChange={() =>
                      isLicensed
                        ? handleSwitchStatus()
                        : onOpenPaidFeatureDialog()
                    }
                    isDisabled={isCoreFeature || isLoading}
                    toggleClassName={cx(
                      isCoreFeature ? "bg-[#D8DAE5] hover:bg-[#D8DAE5]" : ""
                    )}
                  />
                </div>
              )}
            >
              {t(
                {
                  id: "workspace-features-showcase.feature-core-tooltip",
                  defaultMessage: "{name} cannot be disabled in Jugl",
                },
                {
                  name: title,
                }
              )}
            </Tooltip>
          )}
        </div>
        <div className="text-sm leading-[135%] text-[#828282]">
          {description}
          <span
            className="ml-1 cursor-pointer text-primary-800 underline decoration-primary"
            onClick={onReadMoreClick}
          >
            {t({
              id: "common.learn-more",
              defaultMessage: "Learn more",
            })}
          </span>
        </div>
      </div>
    </div>
  );
};
